import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/seo';
import { Loader2, Wrench, Printer, ArrowLeft } from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function RepairReportPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');

  useSEO({
    title: 'Repair Report — DIY X-treme',
    description: 'Printable AI-generated repair report from DIY X-treme.',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/problems/${id}/report`);
        setReport(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not load report.');
      }
    })();
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="industrial-card max-w-md">
          <div className="text-red-400 font-mono text-sm">{err}</div>
          <Link to="/history" className="btn-primary mt-4 text-sm">Back to history</Link>
        </div>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={40} className="text-amber-400 animate-spin" />
      </div>
    );
  }

  const s = report.solution || {};

  return (
    <div className="min-h-screen bg-white text-zinc-900 print:bg-white">
      {/* Print-only styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { padding: 24px !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Toolbar (not printed) */}
      <div className="no-print sticky top-0 z-10 bg-zinc-950 text-zinc-100 border-b border-zinc-800 py-3 px-6 flex items-center justify-between">
        <Link to={`/solution/${id}`} data-testid="report-back-btn" className="inline-flex items-center gap-2 text-zinc-300 hover:text-amber-400 font-mono text-xs uppercase tracking-widest">
          <ArrowLeft size={14} /> Back to solution
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="report-print-btn"
          className="btn-primary text-sm"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-4xl mx-auto print-page px-6 py-10 space-y-6" data-testid="repair-report">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-300 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 flex items-center justify-center">
              <Wrench size={20} className="text-zinc-950" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-2xl tracking-wide">DIY MADE EASY</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Every trade · Every fix · diybot.org</div>
            </div>
          </div>
          <div className="text-right text-xs text-zinc-500 font-mono">
            <div>Report #{id.slice(0, 8).toUpperCase()}</div>
            <div>Generated {fmtDate(report.generated_at)}</div>
          </div>
        </div>

        {/* Customer + problem meta */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Prepared for</div>
            <div className="font-semibold text-zinc-900">{report.customer?.name || 'Customer'}</div>
            <div className="text-zinc-600 text-sm">{report.customer?.email || ''}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Job details</div>
            <div className="text-zinc-900 text-sm">Trade: <span className="font-semibold">{report.problem?.trade || 'General'}</span></div>
            <div className="text-zinc-600 text-sm">Reported: {fmtDate(report.problem?.created_at)}</div>
          </div>
        </div>

        {/* Diagnosis */}
        <section>
          <h2 className="font-display text-2xl tracking-tight border-b border-zinc-300 pb-2 mb-3">Diagnosis</h2>
          <p className="text-zinc-700 text-sm leading-relaxed"><span className="font-semibold">Problem reported:</span> {report.problem?.description || '—'}</p>
          {s.title && <p className="text-zinc-900 text-base mt-2 font-semibold">{s.title}</p>}
          {s.summary && <p className="text-zinc-700 text-sm leading-relaxed mt-1">{s.summary}</p>}
          <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
            {s.estimated_time && <div className="border border-zinc-300 p-2"><div className="font-mono text-[10px] uppercase text-zinc-500">Time</div><div className="font-semibold">{s.estimated_time}</div></div>}
            {s.difficulty && <div className="border border-zinc-300 p-2"><div className="font-mono text-[10px] uppercase text-zinc-500">Difficulty</div><div className="font-semibold">{s.difficulty}</div></div>}
            {s.estimated_cost_usd && <div className="border border-zinc-300 p-2"><div className="font-mono text-[10px] uppercase text-zinc-500">Est. cost</div><div className="font-semibold">{s.estimated_cost_usd}</div></div>}
          </div>
        </section>

        {/* Steps performed */}
        {Array.isArray(s.steps) && s.steps.length > 0 && (
          <section>
            <h2 className="font-display text-2xl tracking-tight border-b border-zinc-300 pb-2 mb-3">Work performed</h2>
            <ol className="space-y-3 text-sm">
              {s.steps.map((st, i) => (
                <li key={st.id || `step-${i}`} className="flex gap-3">
                  <div className="font-display text-xl text-amber-600 shrink-0">{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <div className="font-semibold text-zinc-900">{st.title || st.name || `Step ${i + 1}`}</div>
                    <div className="text-zinc-700 leading-relaxed">{st.detail || st.description || ''}</div>
                    {st.warning && <div className="text-red-700 text-xs mt-1 italic">⚠ {st.warning}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Tools + Materials */}
        {(Array.isArray(s.tools_needed) || Array.isArray(s.materials_needed)) && (
          <div className="grid sm:grid-cols-2 gap-6">
            {Array.isArray(s.tools_needed) && s.tools_needed.length > 0 && (
              <section>
                <h3 className="font-display text-lg tracking-tight border-b border-zinc-300 pb-1 mb-2">Tools used</h3>
                <ul className="list-disc list-inside text-sm text-zinc-700 space-y-1">
                  {s.tools_needed.map((t, i) => (
                    <li key={`t-${i}`}>
                      {typeof t === 'string' ? t : (t.name || t.tool)}
                      {t?.optional && <span className="text-zinc-500 text-xs italic"> (optional)</span>}
                      {t?.note && <span className="text-zinc-500 text-xs"> — {t.note}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {Array.isArray(s.materials_needed) && s.materials_needed.length > 0 && (
              <section>
                <h3 className="font-display text-lg tracking-tight border-b border-zinc-300 pb-1 mb-2">Materials</h3>
                <ul className="text-sm text-zinc-700 space-y-1">
                  {s.materials_needed.map((m, i) => (
                    <li key={`m-${i}`} className="flex justify-between border-b border-dotted border-zinc-200 py-1">
                      <span>{typeof m === 'string' ? m : (m.name || m.item)}{m?.qty ? ` (${m.qty})` : ''}</span>
                      {m.cost_usd || m.cost || m.price ? <span className="font-mono text-xs">{m.cost_usd || m.cost || m.price}</span> : null}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        {/* Safety */}
        {Array.isArray(s.safety_warnings) && s.safety_warnings.length > 0 && (
          <section className="border-l-4 border-red-500 bg-red-50 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-red-700 mb-1">Safety notes</div>
            <ul className="text-red-800 text-sm space-y-1 list-disc list-inside">
              {s.safety_warnings.map((w, i) => <li key={`w-${i}`}>{typeof w === 'string' ? w : (w.text || JSON.stringify(w))}</li>)}
            </ul>
          </section>
        )}

        {/* Notes */}
        {Array.isArray(report.notes) && report.notes.length > 0 && (
          <section>
            <h2 className="font-display text-2xl tracking-tight border-b border-zinc-300 pb-2 mb-3">Field notes</h2>
            <ul className="space-y-2 text-sm">
              {report.notes.map((n) => (
                <li key={n.id} className="border-l-2 border-amber-500 pl-3">
                  <div className="text-zinc-700 whitespace-pre-wrap">{n.text}</div>
                  <div className="font-mono text-[9px] uppercase text-zinc-400 mt-1">{fmtDate(n.created_at)}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Footer */}
        <footer className="border-t border-zinc-300 pt-4 text-center text-xs text-zinc-500 font-mono">
          Generated by DIY X-treme · diybot.org · This report describes work performed by the customer using AI guidance and does not constitute a licensed professional inspection.
        </footer>
      </div>
    </div>
  );
}
